import { LocationType } from '@/entities/location';
import type { ContextGeoSubject } from '@/shared/types/geography.ts';
import { tourFiltersToQuery, useTourFiltersStore } from '@/entities/filter/store/tour';
import { useExpSelections } from '@/entities/expSelection';
import { isValidMovementType, type TMovementType, useMovementTypes } from '@/entities/movement-type';
import type { IExpSelection } from '@/entities/expSelection/model/expSelection.ts';
import type { ExpSelectionsParams } from '@/entities/expSelection/api/useExpSelection.ts';

interface Props {
  slug: string;
  location: LocationType;
  contextGeoSubject: ContextGeoSubject;
  page: number;
  isExtraSelection: boolean;
}

export function useToursSelectionBlock(props: Props) {
  const MIN_EXP_AMOUNT = 2;

  const filterStore = useTourFiltersStore();

  const isLocation = computed(() => ({
    isRegion: props.location === LocationType.REGION,
    isCountry: props.location === LocationType.COUNTRY,
  }));

  const requestParams = computed((): ExpSelectionsParams => {
    const pageParams = {
      region: props.location === LocationType.REGION ? props.slug : undefined,
      country: props.location === LocationType.COUNTRY ? props.slug : undefined,
    };

    const queryParams = tourFiltersToQuery(filterStore.searchState, pageParams, { skipAddTourToType: true });

    const filterParams = {
      ...queryParams,
      date_filter_mode: queryParams?.start_date || queryParams?.end_date ? 'event_date_range' : undefined,
      tag__slugs: filterStore.searchState.tag__slugs !== 'all' ? filterStore.searchState.tag__slugs || '' : '',
      bookable: 'true',
    };

    return {
      ...filterParams,
    };
  });

  const { selections, isFetched } = useExpSelections(requestParams);

  const { movementTypesExpCardLabels } = useMovementTypes();

  const selectionsWithClearedMovementType = computed(() => {
    if (!selections.value) {
      return [] as IExpSelection[];
    }

    return selections.value.map((selection) => ({
      ...selection,
      subtitle: selection.subtitle,
      experiences: selection.experiences.map((experience) => ({
        ...experience,
        movement_type:
          experience.movement_type && isValidMovementType(experience.movement_type)
            ? movementTypesExpCardLabels.value?.[experience.movement_type as TMovementType] || ''
            : '',
      })),
    }));
  });

  const isSelectionVisible = computed(
    () =>
      !isFetched.value ||
      selectionsWithClearedMovementType.value.some((item) => item.experiences.length > MIN_EXP_AMOUNT),
  );

  const isShowTopSelectionBlock = computed(
    () =>
      isSelectionVisible.value &&
      props.page === 1 &&
      (!filterStore.searchState.tag__slugs || filterStore.searchState.tag__slugs === 'all') &&
      !filterStore.isFiltersApplied,
  );

  const isShowExtraSelectionBlock = computed(
    () =>
      isSelectionVisible.value &&
      filterStore.searchState.tag__slugs &&
      filterStore.searchState.tag__slugs !== 'all' &&
      !!filterStore.searchTotalCount,
  );

  const isShowBlock = computed(() =>
    props.isExtraSelection ? isShowExtraSelectionBlock.value : isShowTopSelectionBlock.value,
  );

  return {
    isFetched,
    isLocation,
    selectionsWithClearedMovementType,
    isShowBlock,
  };
}
